"use client";

import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import toast from 'react-hot-toast';
import { getSubdomain } from '@/lib/subdomain';
import { getApiBaseUrl } from '@/lib/api';

interface ClientUser {
    id: number;
    name: string;
    email: string;
    document_number?: string;
    phone?: string;
    avatar_url?: string | null;
    contacts?: any[];   // <--- ADICIONE ESTA LINHA
    addresses?: any[];  // <--- ADICIONE ESTA LINHA
}

interface AuthContextType {
    user: ClientUser | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoadingAuth: boolean;
    login: (login: string, password: string) => Promise<boolean>;
    register: (data: any) => Promise<boolean>;
    logout: () => void;
    fetchUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<ClientUser | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoadingAuth, setIsLoadingAuth] = useState(true);

    const getHeaders = (customToken?: string) => {
        const subdomain = getSubdomain();
        
        return {
            'X-Tenant-Subdomain': subdomain,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${customToken || token}`
        };
    };

    useEffect(() => {
        const storedToken = localStorage.getItem('@AesirShop:token');
        if (storedToken) {
            setToken(storedToken);
            fetchUserData(storedToken);
        } else {
            setIsLoadingAuth(false);
        }
    }, []);

    const fetchUserData = async (currentToken: string | null = token) => {
        if (!currentToken) return;
        
        try {
            const res = await fetch(`${getApiBaseUrl()}/public/client/me`, {
                headers: getHeaders(currentToken)
            });

            if (res.ok) {
                const data = await res.json();
                setUser(data);
            } else {
                logout(); // Token inválido ou expirado
            }
        } catch (error) {
            console.error("Erro ao buscar usuário:", error);
        } finally {
            setIsLoadingAuth(false);
        }
    };

    const login = async (loginParam: string, passwordParam: string): Promise<boolean> => {
        try {
            const res = await fetch(`${getApiBaseUrl()}/public/client/login`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ login: loginParam, password: passwordParam })
            });

            const data = await res.json();

            if (res.ok) {
                localStorage.setItem('@AesirShop:token', data.token);
                setToken(data.token);
                setUser(data.client);
                toast.success('Login realizado com sucesso!');
                return true;
            } else {
                toast.error(data.message || 'Erro ao fazer login.');
                return false;
            }
        } catch (error) {
            toast.error('Erro de conexão com o servidor.');
            return false;
        }
    };

    const register = async (formData: any): Promise<boolean> => {
        try {
            const res = await fetch(`${getApiBaseUrl()}/public/client/register`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify(formData)
            });

            const data = await res.json();

            if (res.ok) {
                localStorage.setItem('@AesirShop:token', data.token);
                setToken(data.token);
                setUser(data.client);
                toast.success('Conta criada com sucesso!');
                return true;
            } else {
                toast.error(data.message || 'Erro ao criar conta.');
                return false;
            }
        } catch (error) {
            toast.error('Erro de conexão com o servidor.');
            return false;
        }
    };

    const logout = () => {
        fetch(`${getApiBaseUrl()}/public/client/logout`, {
            method: 'POST',
            headers: getHeaders()
        }).catch(console.error);

        localStorage.removeItem('@AesirShop:token');
        setToken(null);
        setUser(null);
        toast.success('Você saiu da sua conta.');
    };

    return (
        <AuthContext.Provider value={{ user, token, isAuthenticated: !!user, isLoadingAuth, login, register, logout, fetchUserData }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
    return context;
};